import config from 'constants/config'
import { Experiments } from 'constants/experiments'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { getAge } from 'lib/datetime/dateUtils'
import { getOptimizelyExperimentVariation, getOptimizelyFeatureFlagVariation } from 'lib/optimizely/optimizelyUtils'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import { createSelector } from 'reselect'

export const getDefaultUserDriverCategory = createSelector(
  (state: App.State) => state.auth.account.dob,
  (dateOfBirth): [App.CarHireDriverAgeCategory, number | undefined] => {
    if (dateOfBirth) {
      const years = getAge(dateOfBirth)
      if (years <= 29) {
        return [1, years]
      } else if (years >= 70) {
        return [3, years]
      } else {
        return [2, years]
      }
    }
    // default category
    return [2, undefined]
  },
)

export const isCarHireInsuranceAvailable = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (currentRegionCode) => config.CARHIRE_INSURANCE_ENABLED &&
    config.CARHIRE_INSURANCE_REGIONS.split(',').includes(currentRegionCode),
)

export const isBundledCarHireEnabled = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => getOptimizelyFeatureFlagVariation(state, OptimizelyFeatureFlags.carHireBundledDiscount),
  (currentRegionCode, featureEnabled) =>
    config.CARHIRE_ENABLED &&
    isEnabledForFeature(config.CARHIRE_REGIONS, currentRegionCode) &&
    config.BUNDLED_CAR_HIRE_ENABLED &&
    featureEnabled,
)

export const getCartItemsEligibleForBundledCarHire = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.carHire.bundleEligibilityCartList[state.checkout.cart.cartId],
  (cartItems, cartBundleEligibility): Array<App.Checkout.InstantBookingLEHotelItem | App.Checkout.BedbankHotelItem> => {
    const isCartEligible = cartBundleEligibility?.isEligible
    const earliestItem = cartBundleEligibility?.earliestItem
    const latestItem = cartBundleEligibility?.latestItem
    if (!isCartEligible || !earliestItem || !latestItem) {
      return []
    }

    return cartItems.filter((item): item is App.Checkout.InstantBookingLEHotelItem | App.Checkout.BedbankHotelItem => (
      item.itemId === earliestItem.itemId ||
      item.itemId === latestItem.itemId
    ))
  },
)

export const isCartEligibleForBundledCarHire = createSelector(
  (state: App.State) => getIsCurrentRegionUserLoyaltyRegion(state),
  (state: App.State) => getCartItemsEligibleForBundledCarHire(state),
  (state: App.State) => state.carHire.bundleEligibilityCartList[state.checkout.cart.cartId],
  (isCurrentRegionUserLoyaltyRegion, cartItemsEligibleForBundledCarHire, cartBundleEligibility) => {
    const hasValidItems = cartItemsEligibleForBundledCarHire.length > 0
    const isDiscountBenefitValid = !!cartBundleEligibility?.benefits?.has('discount')
    const isBonusPointsBenefitValid = !!cartBundleEligibility?.benefits?.has('loyalty_bonus_points') && isCurrentRegionUserLoyaltyRegion

    return (
      hasValidItems && (isDiscountBenefitValid || isBonusPointsBenefitValid)
    )
  },
)

export const isCarHireBundledHomepageCarouselEnabled = createSelector(
  (state: App.State) => isBundledCarHireEnabled(state),
  (state: App.State) => !!getOptimizelyExperimentVariation(state, Experiments.carHireBundledDiscountUpcomingOrderHomepageCarousel.id),
  (bundledCarHireEnabled, featureEnabled) => bundledCarHireEnabled && featureEnabled,
)
